import { useState, useEffect, useRef } from 'react';

const FAQ_ITEMS = [
  {
    q: 'episodeの営業時間を教えてください。',
    a: '平日（月〜木）は09:00〜17:00（L.O. 16:30）、土日祝は11:30〜18:00（L.O. 17:30）で営業しています。モーニングは09:00〜11:00（L.O. 10:45）、ランチは15:00まで（L.O. 14:30）です。15:00以降もカフェ営業を続けていますので、コーヒーやスイーツをお楽しみいただけます。定休日は毎週金曜日および祝日の翌日です。臨時休業・変更はInstagramにてお知らせします。',
  },
  {
    q: '定休日はいつですか？',
    a: '毎週金曜日と、祝日の翌日が定休日です。臨時休業や営業時間の変更がある場合はInstagram（@episode_t21）にてお知らせしています。',
  },
  {
    q: 'episodeへのアクセス方法を教えてください。',
    a: '大阪府大阪市北区紅梅町2-3にあります。大阪市営地下鉄「南森町駅」または「大阪天満宮駅」から徒歩約3分です。梅田エリアからもアクセスしやすい立地です。',
  },
  {
    q: 'スペースのレンタルはできますか？',
    a: 'はい、カフェ空間をイベント・展示・ライブ・ポップアップ・ワークショップ・撮影などにレンタルいただけます。フルスペース（着席最大67席・立席約100名）とハーフスペース（16〜22席）の2プランがあります。ご相談はInstagram DM（@episode_t21）にてお気軽にどうぞ。',
  },
  {
    q: 'グルテンフリーのメニューはありますか？',
    a: 'はい、episodeではグルテンフリーのスイーツをご用意しています。オーガニック食材・調味料にこだわった、身体にやさしいメニューを提供しています。',
  },
  {
    q: 'イベントの開催情報はどこで確認できますか？',
    a: '最新のイベント情報はInstagram（@episode_t21）で随時更新しています。過去のイベントや展示の様子はInstagramのハイライトでもご覧いただけます。',
  },
  {
    q: '電話での問い合わせはできますか？',
    a: 'はい、06-6353-3456にてお問い合わせいただけます。スペースレンタルやイベントのご相談はInstagram DM（@episode_t21）でも受け付けています。',
  },
];

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function AnimSection({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const existing = document.getElementById('schema-faq');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-faq';
    script.textContent = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-faq');
      if (el) el.remove();
    };
  }, []);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="py-24 px-6 bg-white">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <AnimSection>
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-0.5 bg-[#A790C4] rounded-full"></div>
              <p className="text-xs tracking-widest text-gray-400 font-light" style={{ letterSpacing: '0.35em' }}>FAQ</p>
              <div className="w-12 h-0.5 bg-[#A790C4] rounded-full"></div>
            </div>
            <h2 className="text-3xl text-gray-900 font-light tracking-wide" style={{ fontFamily: 'serif' }}>
              よくある質問
            </h2>
          </div>
        </AnimSection>

        {/* FAQ Items */}
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <AnimSection key={i} delay={i * 60}>
              <div
                className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
                  openIndex === i
                    ? 'border-[#A790C4]/60 bg-white'
                    : 'border-gray-100 bg-white hover:border-[#A790C4]/30'
                }`}
              >
                {/* Question row */}
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer group"
                  aria-expanded={openIndex === i}
                >
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <span
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mt-0.5"
                      style={{
                        backgroundColor: openIndex === i ? '#A790C4' : '#E6E7E8',
                        color: openIndex === i ? 'white' : '#888',
                        transition: 'background-color 0.3s, color 0.3s',
                      }}
                    >
                      Q
                    </span>
                    <span
                      className="text-sm md:text-base text-gray-800 leading-relaxed font-light"
                      style={{ fontFamily: 'serif' }}
                    >
                      {item.q}
                    </span>
                  </div>
                  <div
                    className="w-7 h-7 flex items-center justify-center flex-shrink-0 ml-4 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: openIndex === i ? '#A790C4' : 'transparent',
                    }}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                        openIndex === i ? 'rotate-180 text-white' : 'text-gray-400'
                      }`}
                    ></i>
                  </div>
                </button>

                {/* Answer */}
                <div
                  style={{
                    maxHeight: openIndex === i ? '400px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                  }}
                >
                  <div className="px-6 pb-5">
                    <div className="flex items-start gap-4">
                      <span
                        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mt-0.5"
                        style={{ backgroundColor: '#386663', color: 'white' }}
                      >
                        A
                      </span>
                      <p
                        className="text-sm text-gray-600 leading-relaxed"
                        style={{ fontFamily: 'serif', lineHeight: '1.9' }}
                      >
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimSection>
          ))}
        </div>

        {/* Bottom note */}
        <AnimSection delay={400}>
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'serif' }}>
              その他のご質問はInstagram DMにてお気軽にどうぞ
            </p>
            <a
              href="https://instagram.com/episode_t21"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#A790C4] hover:text-[#386663] transition-colors duration-300 cursor-pointer text-sm tracking-wider"
              style={{ fontFamily: 'serif' }}
            >
              <i className="ri-instagram-line text-base"></i>
              @episode_t21
            </a>
          </div>
        </AnimSection>
      </div>
    </section>
  );
}